import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useDispatch } from "./useDispatch";
import { updateSettings } from "modules/ConfigurationThunks";
import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  CURRICULUM_LEVEL,
  UPDATE_CURRICULUM_ONLY_SETTINGS,
} from "Constants/permissionConstants";

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const getParsedSettingsValue = params => {
  const {
    settingName,
    storeAtCurriculum,
    storeAtUserAndCourse,
    courseId,
    learningCourseId,
  } = params;
  if (!settingName) return EMPTY_OBJECT;

  try {
    const settingsValue = getSettingValue({
      name: settingName,
      readFromUserAndOrgLevel: !storeAtCurriculum && !storeAtUserAndCourse,
      courseId,
      learningCourseId,
    });
    return settingsValue;
  } catch (error) {
    console.error("Failed to parse settings value:", error);
    return EMPTY_OBJECT;
  }
};

const useConfigureColumns = params => {
  const {
    settingName,
    dashboardName,
    defaultColumns,
    nonConfigurableColumns = EMPTY_OBJECT,
    defaultHiddenColumns = EMPTY_ARRAY,
    storeAtCurriculum = false,
    storeAtUserAndCourse = false,
    key,
    courseId,
    learningCourseId,
  } = params;
  const { start = EMPTY_ARRAY, end = EMPTY_ARRAY } =
    nonConfigurableColumns || EMPTY_OBJECT;
  const dispatch = useDispatch();
  const prevKey = useRef(key);

  const defaultConfigurableColumns = useMemo(() => {
    return _.without(defaultColumns, ...start, ...end);
  }, [defaultColumns, end, start]);

  const updateUserSetting = useCallback(
    updatedColumns => {
      const settingsValue = getParsedSettingsValue({
        settingName,
        storeAtCurriculum,
        storeAtUserAndCourse,
        courseId,
        learningCourseId,
      });

      dispatch(
        updateSettings({
          ...(storeAtCurriculum
            ? {
                queryVariablesSrc: CURRICULUM_LEVEL,
                mutationVariablesSrc: UPDATE_CURRICULUM_ONLY_SETTINGS,
              }
            : {}),
          ...(storeAtUserAndCourse
            ? !_.isEmpty(courseId)
              ? {
                  queryVariablesSrc: "PARENT_AND_COURSE_LEVEL",
                  mutationVariablesSrc: "COURSE_LEVEL",
                  courseId,
                }
              : {
                  queryVariablesSrc: "PARENT_AND_LEARNING_COURSE_LEVEL",
                  mutationVariablesSrc: "LEARNING_COURSE_LEVEL",
                  learningCourseId,
                }
            : {}),
          settings: [
            {
              name: settingName,
              value: JSON.stringify({
                ...settingsValue,
                [dashboardName]: updatedColumns,
              }),
            },
          ],
          showToastMsg: false,
        })
      );
    },
    [
      dashboardName,
      dispatch,
      settingName,
      storeAtCurriculum,
      storeAtUserAndCourse,
      courseId,
      learningCourseId,
    ]
  );

  const userColumns = useMemo(() => {
    const settingsValue = getParsedSettingsValue({
      settingName,
      storeAtCurriculum,
      storeAtUserAndCourse,
      courseId,
      learningCourseId,
    });
    let userConfiguredColumns = settingsValue?.[dashboardName] || EMPTY_ARRAY;

    // Synchronize with latest default columns
    if (!_.isEmpty(userConfiguredColumns)) {
      const userConfiguredColumnsIds = _.map(userConfiguredColumns, "id");

      const addedColumns = _.difference(
        defaultConfigurableColumns,
        userConfiguredColumnsIds
      );

      const removedColumns = _.difference(
        userConfiguredColumnsIds,
        defaultConfigurableColumns
      );

      if (_.size(removedColumns) || _.size(addedColumns)) {
        userConfiguredColumns = _.filter(
          userConfiguredColumns,
          column => !_.includes(removedColumns, column.id)
        );

        userConfiguredColumns = _.concat(
          userConfiguredColumns,
          _.map(addedColumns, columnId => ({
            id: columnId,
            isVisible: !_.includes(defaultHiddenColumns, columnId),
          }))
        );

        updateUserSetting(userConfiguredColumns);
      }
    }

    return userConfiguredColumns;
  }, [
    settingName,
    dashboardName,
    defaultConfigurableColumns,
    updateUserSetting,
    defaultHiddenColumns,
    storeAtCurriculum,
    storeAtUserAndCourse,
    courseId,
    learningCourseId,
  ]);

  const initialColumnConfiguration = useMemo(() => {
    if (_.isEmpty(userColumns)) {
      const initialColumns = _.map(defaultConfigurableColumns, columnId => ({
        id: columnId,
        isVisible: !_.includes(defaultHiddenColumns, columnId),
      }));

      return initialColumns;
    }

    return userColumns;
  }, [defaultConfigurableColumns, defaultHiddenColumns, userColumns]);

  const [userColumnsState, setUserColumnsState] = useState(
    initialColumnConfiguration
  );

  useEffect(() => {
    if (prevKey.current !== key) {
      setUserColumnsState(initialColumnConfiguration);
      prevKey.current = key;
    }
  }, [initialColumnConfiguration, key]);

  const columnIdsForConfigureModal = useMemo(() => {
    return _.map(userColumnsState, "id");
  }, [userColumnsState]);

  const updateColumns = useCallback(
    (params = EMPTY_OBJECT) => {
      const { columnOrder, selectedColumns } = params;

      const updatedColumns = _.map(columnOrder, column => ({
        id: column.id,
        isVisible: _.includes(selectedColumns, column.id),
      }));

      updateUserSetting(updatedColumns);
      setUserColumnsState(updatedColumns);
    },
    [updateUserSetting]
  );

  const updateColumnsState = useCallback((params = EMPTY_OBJECT) => {
    const { columnOrder, selectedColumns } = params;

    const updatedColumns = _.map(columnOrder, column => ({
      id: column.id,
      isVisible: _.includes(selectedColumns, column.id),
    }));

    setUserColumnsState(updatedColumns);
  }, []);

  const currentColumns = useMemo(() => {
    const userColumnIds = _.chain(userColumnsState)
      .filter("isVisible")
      .map("id")
      .value();

    return [...start, ...userColumnIds, ...end];
  }, [end, start, userColumnsState]);

  const allColumns = useMemo(() => {
    const userColumnIds = _.chain(userColumnsState).map("id").value();

    return [...start, ...userColumnIds, ...end];
  }, [end, start, userColumnsState]);

  return {
    columnIdsForConfigureModal,
    updateColumns,
    currentColumns,
    allColumns,
    updateColumnsState,
  };
};

export default useConfigureColumns;
